/* Web Push helpers — registration, permission, subscribe, unsubscribe.
 *
 * The SettingsPage button flow uses these. Safe to call from a component:
 * everything returns plain promises and never throws on "not supported" —
 * it just reports it via pushStatus().
 *
 * Backend contract:
 *   POST   /api/notifications/subscribe         body: { endpoint, keys: {p256dh, auth}, user_agent }
 *   DELETE /api/notifications/subscribe/{id}
 */
import api from "./api";
import { setMeta } from "./offline-db";

const SW_URL = "/service-worker.js";
const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY || "";
const API_BASE = `${import.meta.env.VITE_BACKEND_URL || ""}/api`;

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) arr[i] = raw.charCodeAt(i);
  return arr;
}

export function pushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export function permissionState() {
  if (!pushSupported()) return "unsupported";
  return Notification.permission; // 'granted' | 'denied' | 'default'
}

async function getOrRegisterSW() {
  const existing = await navigator.serviceWorker.getRegistration(SW_URL);
  if (existing) return existing;
  return navigator.serviceWorker.register(SW_URL, { scope: "/" });
}

// Register the SW eagerly on app load so Chrome treats Altiv as installable
// (WebAPK), not just a home-screen shortcut. Without an active SW at first
// paint, "Add to Home Screen" creates a Chrome-badged bookmark.
export async function ensureServiceWorker() {
  if (!("serviceWorker" in navigator)) return null;
  try {
    const reg = await getOrRegisterSW();
    // Stash the API base on EVERY app load so the SW can call /api/offline/snapshot
    // when an OFFLINE_PREFETCH push arrives — its own context has no env vars.
    // Used to live only in enablePush(), but that meant users who enabled push
    // BEFORE offline-mode shipped (2026-05-03) never had `api_base` written —
    // SW would fall back to `self.location.origin/api` (wrong host in prod) and
    // the snapshot fetch would 404 silently.
    try {
      await setMeta("api_base", API_BASE);
    } catch {}
    return reg;
  } catch {
    return null;
  }
}

export async function pushStatus() {
  if (!pushSupported()) {
    return {
      supported: false,
      permission: "unsupported",
      subscribed: false,
      hasVapidKey: !!VAPID_PUBLIC_KEY,
    };
  }
  let subscribed = false;
  let registrationReady = false;
  try {
    const reg = await navigator.serviceWorker.getRegistration(SW_URL);
    registrationReady = !!reg;
    if (reg) {
      const sub = await reg.pushManager.getSubscription();
      subscribed = !!sub;
    }
  } catch {}
  return {
    supported: true,
    permission: Notification.permission,
    subscribed,
    registrationReady,
    hasVapidKey: !!VAPID_PUBLIC_KEY,
  };
}

/**
 * Full opt-in flow:
 *   1. Register the SW
 *   2. Request notification permission (user gesture required — call from onClick)
 *   3. Create a PushSubscription bound to VAPID_PUBLIC_KEY
 *   4. POST it to the backend
 * Returns { ok, reason, subscription_id } — never throws.
 */
export async function enablePush() {
  if (!pushSupported()) return { ok: false, reason: "unsupported" };
  if (!VAPID_PUBLIC_KEY) return { ok: false, reason: "no_vapid_key" };

  let reg;
  try {
    reg = await getOrRegisterSW();
    await navigator.serviceWorker.ready;
  } catch (e) {
    return { ok: false, reason: "sw_register_failed", detail: String(e) };
  }

  // api_base is stashed by ensureServiceWorker() on every app load — no need
  // to repeat it here.

  let permission = Notification.permission;
  if (permission === "default") {
    try {
      permission = await Notification.requestPermission();
    } catch {
      permission = "denied";
    }
  }
  if (permission !== "granted") {
    return { ok: false, reason: "permission_denied" };
  }

  let subscription;
  try {
    subscription = await reg.pushManager.getSubscription();
    if (!subscription) {
      subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      });
    }
  } catch (e) {
    return { ok: false, reason: "subscribe_failed", detail: String(e) };
  }

  const payload = subscription.toJSON();
  try {
    const res = await api.post("/notifications/subscribe", {
      endpoint: payload.endpoint,
      keys: { p256dh: payload.keys.p256dh, auth: payload.keys.auth },
      user_agent: navigator.userAgent,
    });
    return { ok: true, subscription_id: res.data?.subscription_id };
  } catch (e) {
    return { ok: false, reason: "backend_register_failed", detail: String(e) };
  }
}

/** Unsubscribe this device. */
export async function disablePush() {
  if (!pushSupported()) return { ok: false, reason: "unsupported" };
  try {
    const reg = await navigator.serviceWorker.getRegistration(SW_URL);
    if (!reg) return { ok: true };
    const sub = await reg.pushManager.getSubscription();
    if (!sub) return { ok: true };

    // The backend stores subscriptions by id but matches by endpoint too in
    // _get_or_create_prefs (dedup on endpoint). Easiest path: POST-subscribe
    // with the same endpoint to look up its id, then DELETE. But /subscribe
    // dedups and returns the existing id — perfect.
    const payload = sub.toJSON();
    const res = await api.post("/notifications/subscribe", {
      endpoint: payload.endpoint,
      keys: { p256dh: payload.keys.p256dh, auth: payload.keys.auth },
      user_agent: navigator.userAgent,
    });
    const id = res.data?.subscription_id;
    if (id) {
      try {
        await api.delete(`/notifications/subscribe/${id}`);
      } catch {}
    }
    try {
      await sub.unsubscribe();
    } catch {}
    return { ok: true };
  } catch (e) {
    return { ok: false, reason: "disable_failed", detail: String(e) };
  }
}
